// Espace Pro devis / commande → shared PDF render model.
//
// ONE mapper for BOTH pro surfaces:
//   - the web portal   (apps/erp-pro)             → pdfmake  via generatePdfBlob
//   - the mobile app   (apps/tunielec-pro-mobile) → expo-print via render-html
//
// Everything that decides what the pro's client actually sees on the document
// — line mapping, VAT-rate derivation, the notes block, and how the tenant's
// « Modèle Espace Pro » resolves into a design + options — lives here, so the
// two surfaces can never print different papers for the same order.
//
// Constraints (both enforced by the mobile bundler):
//   - NO pdfmake import: Metro would pull in the 1.8 MB vfs font blob.
//   - NO @erp/data-access import: the inputs are structural, so each app can
//     hand over its own DTOs without this lib depending on the data layer.

import type { DesignKey, TemplateOptions } from './template-options';
import type { RenderData } from './designs/design-utils';
import type { PdfClientInfo, PdfDocumentLine } from './types';

// ---------------------------------------------------------------------------
// Structural inputs — the subset of ProDevis / ProCommande the PDF needs.
// ---------------------------------------------------------------------------

export interface ProPdfLine {
  lineNumber: number;
  articleId: string;
  quantite: number;
  pht: number;
  pttc: number;
  articleName?: string | null;
  articleCode?: string | null;
}

export interface ProPdfClient {
  nom: string;
  prenom?: string | null;
  telephone?: string | null;
  adresse?: string | null;
  ville?: string | null;
}

export interface ProPdfDevis {
  code: string;
  date: string;
  pht: number;
  tva: number;
  pttc: number;
  notes?: string | null;
  lines?: ProPdfLine[];
  client?: ProPdfClient | null;
}

export interface ProPdfCommande {
  code: string;
  date: string;
  pht: number;
  tva: number;
  pttc: number;
  deliveryFee: number;
  totalUsed: number;
  totalAPayer: number;
  usedSolde: boolean;
  deliveryAddress?: string | null;
  deliveryCity?: string | null;
  notes?: string | null;
  lines?: ProPdfLine[];
  client?: ProPdfClient | null;
}

/** Resolves an article id to its designation when the line doesn't carry one
 *  (documents created before the repository joined the article identity). */
export type ProArticleLookup = (articleId: string) => { name: string } | null;

/** The tenant's Espace Pro PDF model as exposed by the anon
 *  `pro_public_pdf_template` RPC. Structural on purpose (see header). */
export interface ProPdfTemplateLike {
  designKey: DesignKey;
  options: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Titles / file names — the ONE pro model covers both document kinds, so the
// real per-document title always comes from here, never from the template.
// ---------------------------------------------------------------------------

export const PRO_DEVIS_DOC_TITLE = 'Devis';
export const PRO_COMMANDE_DOC_TITLE = 'Commande';

export function proDevisFileName(code: string): string {
  return `${code}.pdf`;
}

export function proCommandeFileName(code: string): string {
  return `ORD${code}.pdf`;
}

// ---------------------------------------------------------------------------
// Mapping
// ---------------------------------------------------------------------------

/** fr-TN money, `1 234,567 DT`. Grouping uses a plain space on purpose:
 *  fr-TN's narrow no-break space (U+202F) is missing from pdfmake's bundled
 *  Roboto subset and renders as a tofu box past the thousands. */
export function proMoney(amount: number): string {
  if (!Number.isFinite(amount)) return '0,000 DT';
  const fixed = amount.toFixed(3).replace('.', ',');
  const [intPart, decPart] = fixed.split(',');
  const grouped = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped},${decPart} DT`;
}

/** Map a pro line (which carries its own HT / TTC) to a design line. The
 *  per-line VAT rate is derived from the HT→TTC ratio (pttc / pht), which is
 *  reliable no matter what the line's `tva` field holds — checkout stores the
 *  rate there (e.g. 19) while the devis-edit path stored the amount, so reading
 *  `tva` directly would show a wrong rate (e.g. 82%). The tier remise is already
 *  baked into `pht`, so we present the net unit price without a discount column. */
function toLine(l: ProPdfLine, lookup: ProArticleLookup): PdfDocumentLine {
  const qty = Math.max(1, l.quantite);
  const vatRate = l.pht > 0 ? Math.round((l.pttc / l.pht - 1) * 100) : 0;
  return {
    lineNumber: l.lineNumber,
    description: l.articleName ?? lookup(l.articleId)?.name ?? 'Article',
    articleCode: l.articleCode ?? null,
    quantity: l.quantite,
    unitPriceHt: l.pht / qty,
    discountPct: 0,
    vatRate,
    totalHt: l.pht,
  };
}

function toClient(
  c: ProPdfClient | null | undefined,
  delivery?: { address: string | null; city: string | null },
): PdfClientInfo {
  const name = c ? [c.prenom, c.nom].filter(Boolean).join(' ').trim() : '';
  return {
    code: '',
    name: name || '—',
    type: 'individual',
    taxId: null,
    address: delivery?.address ?? c?.adresse ?? null,
    city: delivery?.city ?? c?.ville ?? null,
    phone: c?.telephone ?? null,
  };
}

function joinNotes(parts: (string | null | undefined)[]): string | null {
  const cleaned = parts.filter((p): p is string => !!p && p.trim().length > 0);
  return cleaned.length ? cleaned.join('\n') : null;
}

export function buildProDevisRenderData(
  devis: ProPdfDevis,
  lookupArticle: ProArticleLookup,
): RenderData {
  return {
    docType: 'quote',
    number: devis.code,
    docDate: devis.date,
    validityDate: null,
    client: toClient(devis.client),
    lines: (devis.lines ?? []).map((l) => toLine(l, lookupArticle)),
    totalHt: devis.pht,
    totalDiscount: 0,
    totalVat: devis.tva,
    totalTtc: devis.pttc,
    withholdingAmount: 0,
    // No RAS on pro devis → Total TTC is the final line; skip the redundant
    // "Net à payer" row (only shown when > 0).
    netToPay: 0,
    // Commission is the pro user's internal margin — shown only on-screen,
    // NEVER printed on the client-facing document.
    notes: joinNotes([devis.notes]),
  };
}

export function buildProCommandeRenderData(
  cmd: ProPdfCommande,
  lookupArticle: ProArticleLookup,
): RenderData {
  // Delivery + wallet move the amount due away from the goods TTC; surface the
  // final "Net à payer" only when it actually differs.
  const netDiffers = Math.abs(cmd.totalAPayer - cmd.pttc) > 0.001;
  return {
    docType: 'order',
    number: cmd.code,
    docDate: cmd.date,
    validityDate: null,
    client: toClient(cmd.client, {
      address: cmd.deliveryAddress ?? null,
      city: cmd.deliveryCity ?? null,
    }),
    lines: (cmd.lines ?? []).map((l) => toLine(l, lookupArticle)),
    totalHt: cmd.pht,
    totalDiscount: 0,
    totalVat: cmd.tva,
    totalTtc: cmd.pttc,
    withholdingAmount: 0,
    netToPay: netDiffers ? cmd.totalAPayer : 0,
    // Commission is the pro user's internal margin — shown only on-screen,
    // NEVER printed on the client-facing document.
    notes: joinNotes([
      cmd.notes,
      cmd.deliveryFee > 0 ? `Frais de livraison : ${proMoney(cmd.deliveryFee)}` : null,
      cmd.usedSolde && cmd.totalUsed > 0
        ? `Portefeuille utilisé : -${proMoney(cmd.totalUsed)}`
        : null,
    ]),
  };
}

// ---------------------------------------------------------------------------
// Template resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the tenant's « Modèle Espace Pro » into the (design, options) pair
 * both renderers take. When the tenant hasn't customized one, this is the
 * historical built-in: Tunisien tinted with the pro theme's primary color.
 */
export function resolveProPdfTemplate(input: {
  template?: ProPdfTemplateLike | null;
  primaryColor?: string | null;
}): { designKey: DesignKey; options: Partial<TemplateOptions> } {
  const { template, primaryColor } = input;
  const options: Partial<TemplateOptions> = template
    ? { ...(template.options as Partial<TemplateOptions>) }
    : primaryColor
      ? { primaryColor }
      : {};
  // A model saved from the editor always carries its own primaryColor; a
  // freshly created (never saved) one has empty options — keep the store
  // theme's brand color rather than silently dropping it.
  if (template && options.primaryColor == null && primaryColor) {
    options.primaryColor = primaryColor;
  }
  // The ONE pro model covers both devis and commandes: a custom title would
  // mislabel one of them, so the real per-document title always wins.
  delete options.customTitle;
  return { designKey: template?.designKey ?? 'tunisien', options };
}
